export interface TelegramWebApp {
  initData: string;
  initDataUnsafe: {
    user?: {
      id: number;
      first_name: string;
      last_name?: string;
      username?: string;
      language_code?: string;
    };
    auth_date?: number;
    hash?: string;
  };
  ready: () => void;
  expand: () => void;
  close: () => void;
  themeParams: Record<string, string>;
  colorScheme: "light" | "dark";
  platform?: string;
  version?: string;
  /** Bot API 6.2+ — native OK/Cancel; works on Desktop where window.confirm often breaks. */
  showConfirm?: (message: string, callback?: (ok: boolean) => void) => void;
  /** Bot API 6.2+ — native alert; prefer over window.alert in Mini Apps. */
  showAlert?: (message: string, callback?: () => void) => void;
  showPopup?: (
    params: {
      title?: string;
      message: string;
      buttons?: Array<{
        id?: string;
        type?: "default" | "ok" | "close" | "cancel" | "destructive";
        text?: string;
      }>;
    },
    callback?: (buttonId: string) => void,
  ) => void;
  MainButton: {
    text: string;
    show: () => void;
    hide: () => void;
    onClick: (cb: () => void) => void;
    offClick: (cb: () => void) => void;
  };
  setHeaderColor: (color: string) => void;
  setBackgroundColor: (color: string) => void;
  setBottomBarColor?: (color: string) => void;
  onEvent?: (event: string, callback: () => void) => void;
  offEvent?: (event: string, callback: () => void) => void;
  BackButton?: {
    isVisible?: boolean;
    show: () => void;
    hide: () => void;
    onClick: (cb: () => void) => void;
    offClick: (cb: () => void) => void;
  };
  SecondaryButton?: {
    hide: () => void;
  };
  /** Bot API 6.1+ — tactile feedback inside Mini Apps */
  HapticFeedback?: {
    impactOccurred: (
      style: "light" | "medium" | "heavy" | "rigid" | "soft",
    ) => void;
    notificationOccurred: (type: "error" | "success" | "warning") => void;
    selectionChanged: () => void;
  };
}

declare global {
  interface Window {
    Telegram?: { WebApp?: TelegramWebApp };
  }
}

export function getTelegramWebApp(): TelegramWebApp | null {
  return window.Telegram?.WebApp ?? null;
}

/** True only when Telegram injected signed initData (real Mini App session). */
export function isTelegramMiniApp(): boolean {
  const wa = getTelegramWebApp();
  return Boolean(wa?.initData && wa.initData.length > 0);
}

/** Telegram client shell present (may still lack initData if opened wrong). */
export function isInsideTelegram(): boolean {
  const wa = getTelegramWebApp();
  if (!wa) return false;
  // platform is set inside Telegram clients; also detect via user agent fallback
  if (wa.platform && wa.platform !== "unknown") return true;
  return /Telegram/i.test(navigator.userAgent);
}

export function initTelegramUI() {
  const wa = getTelegramWebApp();
  if (!wa) return;
  try {
    wa.ready();
    wa.expand();
  } catch {
    /* older clients */
  }
}

/**
 * Confirmation dialog that works inside Telegram Mini Apps (incl. Desktop).
 * Desktop WebView often breaks synchronous window.confirm (dialog shows but
 * OK never returns true). Use Telegram.WebApp.showConfirm when available.
 */
export function tgConfirm(message: string): Promise<boolean> {
  const wa = getTelegramWebApp();
  if (typeof wa?.showConfirm === "function") {
    return new Promise((resolve) => {
      try {
        wa.showConfirm!(message, (ok) => resolve(Boolean(ok)));
      } catch {
        resolve(window.confirm(message));
      }
    });
  }
  return Promise.resolve(window.confirm(message));
}

/** Alert that uses Telegram native UI when available (Desktop-safe). */
export function tgAlert(message: string): Promise<void> {
  const wa = getTelegramWebApp();
  if (typeof wa?.showAlert === "function") {
    return new Promise((resolve) => {
      try {
        wa.showAlert!(message, () => resolve());
      } catch {
        window.alert(message);
        resolve();
      }
    });
  }
  window.alert(message);
  return Promise.resolve();
}

/** Light tactile feedback; no-ops outside Telegram or on older clients. */
export function tgHapticImpact(
  style: "light" | "medium" | "heavy" | "rigid" | "soft" = "medium",
) {
  try {
    getTelegramWebApp()?.HapticFeedback?.impactOccurred?.(style);
  } catch {
    /* older clients / non-Telegram */
  }
}

export function tgHapticNotification(
  type: "error" | "success" | "warning" = "success",
) {
  try {
    getTelegramWebApp()?.HapticFeedback?.notificationOccurred?.(type);
  } catch {
    /* older clients / non-Telegram */
  }
}

export function getTelegramDebugInfo() {
  const wa = getTelegramWebApp();
  return {
    hasWebApp: Boolean(wa),
    platform: wa?.platform ?? null,
    version: wa?.version ?? null,
    hasInitData: Boolean(wa?.initData),
    initDataLength: wa?.initData?.length ?? 0,
    hasUser: Boolean(wa?.initDataUnsafe?.user),
    userId: wa?.initDataUnsafe?.user?.id ?? null,
    host: window.location.host,
    origin: window.location.origin,
    apiUrl: (import.meta.env.VITE_API_URL as string | undefined) ?? "(unset)",
    botUsername:
      (import.meta.env.VITE_TELEGRAM_BOT_USERNAME as string | undefined) ??
      "(unset)",
  };
}
